import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  Search, Loader2, TrendingUp, BarChart3, DollarSign, Zap, Info,
  Plus, Trash2, Play, Pause, Globe, BookmarkPlus, ArrowRight, Check,
  RefreshCw, Download
} from "lucide-react";

const INTENT_COLORS = {
  transactional: { bg: "rgba(16,185,129,0.12)", color: "#10b981" },
  commercial: { bg: "rgba(251,191,36,0.12)", color: "#fbbf24" },
  informational: { bg: "rgba(59,130,246,0.12)", color: "#60a5fa" },
  navigational: { bg: "rgba(167,139,250,0.12)", color: "#a78bfa" },
};

export default function KeywordsTab({ selectedProject }) {
  const { api } = useAuth();
  const [credits, setCredits] = useState(null);
  const [activeSection, setActiveSection] = useState("lookup");

  // Lookup state
  const [keywords, setKeywords] = useState("");
  const [lookupResults, setLookupResults] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Related state
  const [relatedSeed, setRelatedSeed] = useState("");
  const [relatedResults, setRelatedResults] = useState(null);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [shortlisted, setShortlisted] = useState(new Set());

  // URL traffic state
  const [urlDomain, setUrlDomain] = useState("");
  const [urlResults, setUrlResults] = useState(null);
  const [urlLoading, setUrlLoading] = useState(false);

  // Saved keywords state
  const [savedKeywords, setSavedKeywords] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [importing, setImporting] = useState({});

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchCredits = useCallback(async () => {
    try { const r = await api.get("/keywords/credits"); setCredits(r.data.credits); } catch {}
  }, [api]);

  const fetchSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const params = selectedProject ? { project_id: selectedProject } : {};
      const r = await api.get("/keywords/saved", { params });
      setSavedKeywords(r.data);
    } catch {} finally { setSavedLoading(false); }
  }, [api, selectedProject]);

  useEffect(() => { fetchCredits(); fetchSaved(); }, [fetchCredits, fetchSaved]);

  const formatVol = (v) => {
    if (!v) return "0";
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return v;
  };

  // ---- Lookup ----
  const handleLookup = async () => {
    if (!keywords.trim()) return;
    setLookupLoading(true); setError("");
    try {
      const kws = keywords.split(",").map(k => k.trim()).filter(Boolean);
      const res = await api.post("/keywords/lookup", { keywords: kws });
      setLookupResults(res.data);
      fetchCredits();
    } catch (err) { setError(err.response?.data?.detail || "Lookup failed"); }
    finally { setLookupLoading(false); }
  };

  // ---- Related Keywords ----
  const handleRelated = async () => {
    if (!relatedSeed.trim()) return;
    setRelatedLoading(true); setError(""); setShortlisted(new Set());
    try {
      const res = await api.post("/keywords/related", { keyword: relatedSeed.trim(), num: 30 });
      setRelatedResults(res.data);
      fetchCredits();
    } catch (err) { setError(err.response?.data?.detail || "Failed"); }
    finally { setRelatedLoading(false); }
  };

  const toggleShortlist = (kw) => {
    setShortlisted(prev => {
      const next = new Set(prev);
      next.has(kw) ? next.delete(kw) : next.add(kw);
      return next;
    });
  };

  const handleSaveShortlisted = async () => {
    if (!shortlisted.size) return;
    try {
      await api.post("/keywords/save", { keywords: [...shortlisted], project_id: selectedProject || "" });
      setSuccess(`${shortlisted.size} keywords saved!`);
      setShortlisted(new Set());
      fetchSaved();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.response?.data?.detail || "Save failed"); }
  };

  // ---- URL Traffic ----
  const handleUrlTraffic = async () => {
    if (!urlDomain.trim()) return;
    setUrlLoading(true); setError("");
    try {
      const res = await api.post("/keywords/url-traffic", { domain: urlDomain.trim(), num: 20 });
      setUrlResults(res.data);
      fetchCredits();
    } catch (err) { setError(err.response?.data?.detail || "Failed"); }
    finally { setUrlLoading(false); }
  };

  // ---- Saved Keywords Actions ----
  const handleToggleCron = async (kw) => {
    try {
      await api.patch(`/keywords/saved/${encodeURIComponent(kw.keyword)}/toggle-cron`, null, {
        params: { project_id: kw.project_id || "" }
      });
      fetchSaved();
    } catch (err) { setError(err.response?.data?.detail || "Toggle failed"); }
  };

  const handleRemoveSaved = async (kw) => {
    try {
      await api.delete(`/keywords/saved/${encodeURIComponent(kw.keyword)}`, {
        params: { project_id: kw.project_id || "" }
      });
      fetchSaved();
    } catch {}
  };

  const pollImportStatus = async (taskId, keyword, maxWait = 660000) => {
    const start = Date.now();
    while (Date.now() - start < maxWait) {
      await new Promise(r => setTimeout(r, 5000));
      try {
        const res = await api.get(`/keywords/import-status/${taskId}`);
        if (res.data.status === "completed") {
          return res.data;
        }
        if (res.data.status === "failed") {
          throw new Error(res.data.error || "Import failed");
        }
      } catch (err) {
        if (err.response?.status === 404) continue;
        throw err;
      }
    }
    return { status: "timeout", imported: 0, duplicates: 0 };
  };

  const handleTriggerImport = async (kw, source = "recent-posts") => {
    setImporting(p => ({ ...p, [kw.keyword]: true }));
    setError("");
    setSuccess(`Importing "${kw.keyword}" — this may take a few minutes...`);
    try {
      const res = await api.post("/keywords/trigger-import", {
        keyword: kw.keyword, source, project_id: kw.project_id || selectedProject || "",
      });
      if (res.data.task_id) {
        const result = await pollImportStatus(res.data.task_id, kw.keyword);
        if (result.status === "completed") {
          if (result.error) {
            setError(`Scraper issue for "${kw.keyword}": ${result.error}`);
            setSuccess("");
          } else {
            setSuccess(`Imported ${result.imported} posts for "${kw.keyword}" (${result.duplicates} dupes skipped)`);
          }
        } else if (result.status === "timeout") {
          setSuccess(`Import for "${kw.keyword}" is still running in background. Check back soon.`);
        } else {
          setError(result.error || "Import failed");
        }
      } else {
        setSuccess(`Imported ${res.data.imported} posts for "${kw.keyword}" (${res.data.duplicates} dupes skipped)`);
      }
      fetchSaved();
      setTimeout(() => setSuccess(""), 6000);
    } catch (err) {
      setError(err.message || err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(p => ({ ...p, [kw.keyword]: false }));
    }
  };

  const handleRunCron = async () => {
    setImporting(p => ({ ...p, _cron: true }));
    setError("");
    setSuccess("Running cron imports in background — this may take several minutes...");
    try {
      const res = await api.post("/keywords/run-cron", null, {
        params: selectedProject ? { project_id: selectedProject } : {}
      });
      if (res.data.task_ids && res.data.task_ids.length > 0) {
        const results = await Promise.all(
          res.data.task_ids.map(tid => pollImportStatus(tid, "cron").catch(() => ({ imported: 0 })))
        );
        const total = results.reduce((s, r) => s + (r.imported || 0), 0);
        setSuccess(`Cron completed: ${total} new posts imported across ${res.data.processed} keywords`);
      } else {
        setSuccess(res.data.message || "No keywords with active cron");
      }
      fetchSaved();
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) { setError(err.response?.data?.detail || "Cron failed"); }
    finally { setImporting(p => ({ ...p, _cron: false })); }
  };

  const sections = [
    { id: "lookup", label: "Keyword Lookup", icon: Search },
    { id: "related", label: "Related + Intent", icon: TrendingUp },
    { id: "traffic", label: "URL Traffic", icon: Globe },
    { id: "saved", label: "Saved Keywords", icon: BookmarkPlus },
  ];

  return (
    <div className="keywords-section" data-testid="keywords-section">
      <div className="section-header">
        <h2 className="section-title"><TrendingUp size={20} color="#ff4500" /> Keyword Research</h2>
        {credits !== null && (
          <span className="credits-badge" data-testid="ke-credits">
            <Zap size={12} /> {credits.toLocaleString()} credits
          </span>
        )}
      </div>

      {error && <div className="alert alert-error" style={{ margin: '8px 0' }}>{error} <button onClick={() => setError("")} className="alert-close" style={{background:'none',border:'none',color:'inherit',cursor:'pointer'}}>&times;</button></div>}
      {success && <div className="alert alert-success" style={{ margin: '8px 0' }}>{success}</div>}

      <div className="mode-tabs" style={{ marginBottom: 16 }}>
        {sections.map(s => (
          <button key={s.id} className={`mode-tab ${activeSection === s.id ? 'active' : ''}`}
            onClick={() => setActiveSection(s.id)} data-testid={`kw-section-${s.id}`}>
            <s.icon size={14} /> {s.label}
          </button>
        ))}
      </div>

      {/* ---- KEYWORD LOOKUP ---- */}
      {activeSection === "lookup" && (
        <div data-testid="keyword-lookup">
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            Look up search volume, CPC, and competition for keywords.
          </p>
          <div className="search-bar">
            <input className="filter-input search-keyword-input" placeholder="Enter keywords (comma-separated)"
              value={keywords} onChange={e => setKeywords(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLookup()} data-testid="keyword-lookup-input" />
            <button className="btn btn-primary" onClick={handleLookup} disabled={lookupLoading || !keywords.trim()} data-testid="keyword-lookup-btn">
              {lookupLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {lookupLoading ? 'Looking up...' : 'Lookup'}
            </button>
          </div>
          {lookupResults?.data && (
            <div className="keyword-results" data-testid="keyword-results" style={{ marginTop: 16 }}>
              <div className="results-info"><span>{lookupResults.data.length} keywords analyzed</span></div>
              <div className="table-container">
                <table className="comments-table">
                  <thead><tr><th>Keyword</th><th>Volume</th><th>CPC</th><th>Competition</th><th>Trend</th></tr></thead>
                  <tbody>
                    {lookupResults.data.sort((a, b) => b.vol - a.vol).map((kw, i) => (
                      <tr key={i}>
                        <td><strong style={{ color: '#e5e5e5' }}>{kw.keyword}</strong></td>
                        <td><span className={`vol-badge ${kw.vol >= 10000 ? 'high' : kw.vol >= 1000 ? 'mid' : 'low'}`}><BarChart3 size={11} /> {formatVol(kw.vol)}</span></td>
                        <td style={{ fontFamily: 'JetBrains Mono' }}><DollarSign size={11} />{kw.cpc?.value || '0'}</td>
                        <td>
                          <div className="competition-bar"><div className="competition-fill" style={{ width: `${(kw.competition || 0) * 100}%` }}></div></div>
                          <span style={{ fontSize: 11, color: '#6b7280' }}>{((kw.competition || 0) * 100).toFixed(0)}%</span>
                        </td>
                        <td>
                          {kw.trend?.length > 0 && (
                            <div className="mini-trend">
                              {kw.trend.slice(-6).map((t, j) => (
                                <div key={j} className="trend-bar" style={{ height: `${Math.max(4, (t.value / Math.max(...kw.trend.map(tt => tt.value || 1))) * 24)}px` }}
                                  title={`${t.month} ${t.year}: ${formatVol(t.value)}`}></div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---- RELATED KEYWORDS + INTENT ---- */}
      {activeSection === "related" && (
        <div data-testid="related-keywords">
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            Find related keywords with search intent classification. Shortlist keywords to save for imports.
          </p>
          <div className="search-bar">
            <input className="filter-input search-keyword-input" placeholder="Enter a seed keyword (e.g. windows product key)"
              value={relatedSeed} onChange={e => setRelatedSeed(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRelated()} data-testid="related-seed-input" />
            <button className="btn btn-primary" onClick={handleRelated} disabled={relatedLoading || !relatedSeed.trim()} data-testid="related-search-btn">
              {relatedLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Find Related
            </button>
            {shortlisted.size > 0 && (
              <button className="btn btn-ai" onClick={handleSaveShortlisted} data-testid="save-shortlisted-btn">
                <BookmarkPlus size={14} /> Save {shortlisted.size} Keywords
              </button>
            )}
          </div>

          {relatedResults && (
            <div style={{ marginTop: 16 }}>
              {relatedResults.seed_data?.vol !== undefined && (
                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
                  Seed: <strong style={{ color: '#ff4500' }}>{relatedResults.seed_keyword}</strong> — Vol: {formatVol(relatedResults.seed_data.vol)}
                  {relatedResults.credits_used > 0 && <span style={{ marginLeft: 8, color: '#6b7280' }}>({relatedResults.credits_used} credits used)</span>}
                </div>
              )}
              <div className="table-container">
                <table className="comments-table">
                  <thead><tr><th style={{width:30}}></th><th>Keyword</th><th>Intent</th><th>Volume</th><th>CPC</th><th>Competition</th></tr></thead>
                  <tbody>
                    {relatedResults.keywords?.map((kw, i) => (
                      <tr key={i} style={shortlisted.has(kw.keyword) ? { background: 'rgba(255,69,0,0.06)' } : {}}>
                        <td>
                          <div className={`custom-checkbox ${shortlisted.has(kw.keyword) ? 'checked' : ''}`}
                            onClick={() => toggleShortlist(kw.keyword)} style={{ cursor: 'pointer' }}>
                            {shortlisted.has(kw.keyword) && <Check size={12} color="white" />}
                          </div>
                        </td>
                        <td><strong style={{ color: '#e5e5e5' }}>{kw.keyword}</strong></td>
                        <td>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                            background: INTENT_COLORS[kw.intent]?.bg || '#1e1e21',
                            color: INTENT_COLORS[kw.intent]?.color || '#6b7280' }}>
                            {kw.intent}
                          </span>
                        </td>
                        <td><span className={`vol-badge ${kw.vol >= 10000 ? 'high' : kw.vol >= 1000 ? 'mid' : 'low'}`}>{formatVol(kw.vol)}</span></td>
                        <td style={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}>${kw.cpc || '0'}</td>
                        <td>
                          <div className="competition-bar"><div className="competition-fill" style={{ width: `${(kw.competition || 0) * 100}%` }}></div></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---- URL TRAFFIC ---- */}
      {activeSection === "traffic" && (
        <div data-testid="url-traffic">
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            Enter a domain for organic traffic overview, or a full Reddit post URL for per-page organic traffic.
          </p>
          <div className="search-bar">
            <input className="filter-input search-keyword-input" placeholder="Enter a domain or full URL (e.g. logkeys.com or reddit.com/r/...)"
              value={urlDomain} onChange={e => setUrlDomain(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleUrlTraffic()} data-testid="url-traffic-input" />
            <button className="btn btn-primary" onClick={handleUrlTraffic} disabled={urlLoading || !urlDomain.trim()} data-testid="url-traffic-btn">
              {urlLoading ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />}
              Analyze
            </button>
          </div>

          {urlResults && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <div className="report-stat-card" style={{ flex: 1 }}>
                  <div className="report-stat-label">Domain</div>
                  <div className="report-stat-value" style={{ color: '#ff4500', fontSize: 18 }}>{urlResults.domain}</div>
                </div>
                <div className="report-stat-card" style={{ flex: 1 }}>
                  <div className="report-stat-label">Est. Monthly Traffic</div>
                  <div className="report-stat-value" style={{ color: '#10b981' }}>{formatVol(urlResults.total_estimated_traffic)}</div>
                </div>
                <div className="report-stat-card" style={{ flex: 1 }}>
                  <div className="report-stat-label">Keywords Found</div>
                  <div className="report-stat-value">{urlResults.keywords?.length || 0}</div>
                </div>
                <div className="report-stat-card" style={{ flex: 1 }}>
                  <div className="report-stat-label">Credits Used</div>
                  <div className="report-stat-value" style={{ color: '#fbbf24' }}>{urlResults.credits_used}</div>
                </div>
                {urlResults.source && (
                  <div className="report-stat-card" style={{ flex: 1 }}>
                    <div className="report-stat-label">Source</div>
                    <div className="report-stat-value" style={{ color: urlResults.source === 'ahrefs' ? '#a78bfa' : '#60a5fa', fontSize: 14, textTransform: 'capitalize' }}>{urlResults.source}</div>
                  </div>
                )}
              </div>
              {urlResults.keywords?.length > 0 && (
                <div className="table-container">
                  <table className="comments-table">
                    <thead><tr><th>Keyword</th><th>Est. Monthly Traffic</th><th>SERP Position</th></tr></thead>
                    <tbody>
                      {urlResults.keywords.map((kw, i) => {
                        const traffic = kw.estimated_monthly_traffic ?? kw.volume ?? kw.traffic ?? 0;
                        const pos = kw.serp_position ?? kw.position ?? 0;
                        return (
                          <tr key={i}>
                            <td><strong style={{ color: '#e5e5e5' }}>{kw.keyword}</strong></td>
                            <td>
                              <span className={`vol-badge ${traffic >= 1000 ? 'high' : traffic >= 100 ? 'mid' : 'low'}`}>
                                <TrendingUp size={11} /> {formatVol(traffic)}
                              </span>
                            </td>
                            <td>
                              <span className={`rank-badge ${pos <= 3 ? 'top' : pos <= 10 ? 'good' : ''}`}>
                                #{pos}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---- SAVED KEYWORDS ---- */}
      {activeSection === "saved" && (
        <div data-testid="saved-keywords">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
              Manage your shortlisted keywords. Toggle cron to auto-import, or manually trigger imports.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              {savedKeywords.some(k => k.cron_active) && (
                <button className="btn btn-active-scheduler" onClick={handleRunCron}
                  disabled={importing._cron} data-testid="run-cron-btn">
                  {importing._cron ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                  Run Cron Now
                </button>
              )}
              <button className="btn btn-sm btn-secondary" onClick={fetchSaved}><RefreshCw size={14} /></button>
            </div>
          </div>

          {savedLoading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : savedKeywords.length === 0 ? (
            <div className="empty-state">
              <BookmarkPlus size={48} />
              <div className="empty-text">No saved keywords yet</div>
              <div className="empty-hint">Use "Related + Intent" tab to find and shortlist keywords</div>
            </div>
          ) : (
            <div className="table-container">
              <table className="comments-table">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Cron</th>
                    <th>Imports</th>
                    <th>Last Import</th>
                    <th>Saved By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedKeywords.map((kw, i) => (
                    <tr key={i}>
                      <td><strong style={{ color: '#e5e5e5' }}>{kw.keyword}</strong></td>
                      <td>
                        <button className={`btn btn-sm ${kw.cron_active ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => handleToggleCron(kw)} data-testid={`cron-toggle-${i}`}
                          title={kw.cron_active ? 'Cron active - click to pause' : 'Click to enable auto-import'}>
                          {kw.cron_active ? <Play size={12} /> : <Pause size={12} />}
                          {kw.cron_active ? 'Active' : 'Paused'}
                        </button>
                      </td>
                      <td style={{ fontFamily: 'JetBrains Mono', fontSize: 12 }}>{kw.import_count || 0}</td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>
                        {kw.last_import_at ? new Date(kw.last_import_at).toLocaleDateString() : 'Never'}
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>{kw.saved_by}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm btn-primary" onClick={() => handleTriggerImport(kw, "recent-posts")}
                            disabled={importing[kw.keyword]} data-testid={`import-recent-${i}`}
                            title="Import recent posts">
                            {importing[kw.keyword] ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                            Recent
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleTriggerImport(kw, "high-engagement")}
                            disabled={importing[kw.keyword]} title="Import high engagement posts">
                            <ArrowRight size={12} /> Hot
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleTriggerImport(kw, "google-search")}
                            disabled={importing[kw.keyword]} title="Import from Google search">
                            <Globe size={12} /> Google
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleRemoveSaved(kw)} title="Remove keyword">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="keyword-info-box" style={{ marginTop: 20 }}>
        <Info size={14} color="#6b7280" />
        <span>Tip: Target keywords with high volume and low competition for best marketing results. Shortlist keywords from "Related + Intent" tab, then manage imports from "Saved Keywords".</span>
      </div>
    </div>
  );
}
